/**
 * SharePanel — Inline popover replacement for ShareLeadModal.
 *
 * Wires to existing POST /api/v1/admin/share-lead with {leadId, recipientEmail,
 * fields[], note}. Same backend contract; new UI.
 *
 * Team vs External toggle. 5 field-picker chips. Note textarea.
 */
import { useState } from 'react';
import { Send } from 'lucide-react';
import { Btn } from '../ui/primitives';

const SHARE_FIELDS = [
  { key: 'name', label: 'Name', backend: ['first_name', 'last_name'] },
  { key: 'email', label: 'Email', backend: ['email'] },
  { key: 'phone', label: 'Phone', backend: ['phone'] },
  { key: 'business', label: 'Business', backend: ['business_name', 'industry'] },
  { key: 'status', label: 'Status & grade', backend: ['status', 'grade', 'score'] },
];

export default function SharePanel({ leadName, onClose, onShare, isLoading, teamMembers = [] }) {
  const [mode, setMode] = useState('external'); // 'team' | 'external'
  const [recipientEmail, setRecipientEmail] = useState('');
  const [teamMemberId, setTeamMemberId] = useState('');
  const [enabledFields, setEnabledFields] = useState({ name: true, email: true, phone: true, business: false, status: false });
  const [note, setNote] = useState('');

  const toggle = (k) => setEnabledFields((p) => ({ ...p, [k]: !p[k] }));

  const handleShare = () => {
    const backendFields = SHARE_FIELDS.filter((f) => enabledFields[f.key]).flatMap((f) => f.backend);
    let email = recipientEmail;
    if (mode === 'team') {
      const m = teamMembers.find((tm) => tm.id === teamMemberId);
      email = m?.email || '';
    }
    if (!email) return;
    onShare({ recipientEmail: email, fields: backendFields, note });
  };

  const canSend = (mode === 'external' ? recipientEmail.includes('@') : !!teamMemberId) && Object.values(enabledFields).some(Boolean);

  return (
    <div data-testid="share-panel" style={{ minWidth: 340, maxWidth: 400 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>Share {leadName || 'lead'}</h4>
      <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--muted)' }}>Only the selected fields will be sent.</p>

      <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface-3)', borderRadius: 99, marginBottom: 12, border: '1px solid var(--border)' }}>
        <button
          data-testid="share-mode-external"
          onClick={() => setMode('external')}
          style={{ flex: 1, border: 'none', background: mode === 'external' ? 'var(--surface)' : 'transparent', padding: '6px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700, color: mode === 'external' ? 'var(--text)' : 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}
        >External email</button>
        <button
          data-testid="share-mode-team"
          onClick={() => setMode('team')}
          style={{ flex: 1, border: 'none', background: mode === 'team' ? 'var(--surface)' : 'transparent', padding: '6px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700, color: mode === 'team' ? 'var(--text)' : 'var(--muted)', cursor: 'pointer', fontFamily: 'inherit' }}
        >Team member</button>
      </div>

      {mode === 'external' ? (
        <input
          data-testid="share-recipient-email"
          className="lb-inp"
          type="email"
          placeholder="recipient@example.com"
          value={recipientEmail}
          onChange={(ev) => setRecipientEmail(ev.target.value)}
          style={{ marginBottom: 12 }}
        />
      ) : (
        <select
          data-testid="share-recipient-team"
          className="lb-field-select"
          value={teamMemberId}
          onChange={(ev) => setTeamMemberId(ev.target.value)}
          style={{ width: '100%', marginBottom: 12, height: 38 }}
        >
          <option value="">Select team member…</option>
          {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.full_name || m.email}</option>)}
        </select>
      )}

      <span className="lb-lbl">Fields to share</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {SHARE_FIELDS.map((f) => {
          const on = enabledFields[f.key];
          return (
            <button
              key={f.key}
              data-testid={`share-field-${f.key}`}
              onClick={() => toggle(f.key)}
              style={{
                padding: '5px 11px', borderRadius: 99,
                border: `1px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                background: on ? 'var(--accent)' : 'var(--surface-2)',
                color: on ? '#fff' : 'var(--text-2)',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <span className="lb-lbl">Note (optional)</span>
      <textarea
        data-testid="share-note"
        className="lb-inp"
        rows={2}
        placeholder="Why are you sharing this lead?"
        value={note}
        onChange={(ev) => setNote(ev.target.value)}
        style={{ marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Btn data-testid="share-panel-btn-2" variant="ghost" sm onClick={onClose} testid="share-cancel">Cancel</Btn>
        <Btn data-testid="share-panel-btn-1" variant="primary" sm icon={Send} onClick={handleShare} disabled={!canSend || isLoading} testid="share-confirm">
          {isLoading ? 'Sending…' : 'Share'}
        </Btn>
      </div>
    </div>
  );
}
